import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { Controller, FieldArrayWithId, UseFieldArrayRemove } from 'react-hook-form';

import { InfoPopover } from 'clipsal-cortex-ui/src/components/InfoPopover';

import { CommonFieldListProps, SystemDetailsFormData } from './system-details-form-types';
import { useGetContactorControlledHotWaterDevices } from './useGetContactorControlledHotWaterDevices';

export type HotWaterFieldArrayProps = {
  hotWaterFields: FieldArrayWithId<SystemDetailsFormData, 'hotWaterDevices', 'id'>[];
  removeHotWaterDevice: UseFieldArrayRemove;
};

type HotWaterFormProps = CommonFieldListProps & HotWaterFieldArrayProps;

export function HotWaterForm(props: HotWaterFormProps) {
  const { control, register, removeHotWaterDevice, errors, hotWaterFields: fields } = props;
  const toast = useToast();

  const { contactorControlledHotWaterDevices, hasUnexpectedUnlinkedSwitch, isLoading } =
    useGetContactorControlledHotWaterDevices();

  const [currentlyEditingIndex, setCurrentlyEditingIndex] = useState(0);
  // In the case that there are multiple devices set up to a contactor with `load_other`, then we need to ask the installer which is the hot water system.
  // This is for handling the case of Catch sites that have only been partially set up.
  // It is based on the assumption that there will only ever be one hot water system on a site (at least only one that is contactor-controlled).
  const hasMultipleUnconfirmedDevices = useMemo(() => {
    return fields.length >= 2 && fields.every((device) => !device.hotWaterType);
  }, [fields]);

  if (!fields.length && !hasUnexpectedUnlinkedSwitch) return null;

  return (
    <Box my={8}>
      <Flex align="center">
        <Heading size={'lg'} mb={4} ml={[4, 6]}>
          Contactor-Controlled Hot Water Devices
        </Heading>
      </Flex>

      {hasUnexpectedUnlinkedSwitch && (
        <Alert status="warning">
          <AlertIcon />
          <Box data-testid="hot-water-unlinked-switch-warning">
            This site includes one or more contactors that are not connected to a load in our system. If this is
            unexpected, please return to the CATCH Configurator app and make sure you create the connection between the
            contactor and the matching load (e.g. hot water load).
          </Box>
        </Alert>
      )}

      <Accordion
        index={currentlyEditingIndex}
        onChange={(index: number) => setCurrentlyEditingIndex(index)}
        allowToggle
      >
        {fields.map((field, index) => {
          const matchingDevice = contactorControlledHotWaterDevices.find(
            (device) => device.applianceId === field.applianceId
          );
          return (
            <AccordionItem data-testid={`hot-water-form-${index}`} key={`${field.id}-${index}`} my={2}>
              <AccordionButton data-testid={`hot-water-${index}`} role={'button'} as={Box}>
                <Flex w={'100%'} justify={'space-between'} align={'center'}>
                  <Box>
                    <Heading size={'md'}>Hot Water {index + 1}</Heading>
                    <Box mt={1}>
                      {isLoading ? (
                        <Spinner size={'xs'} emptyColor="gray.200" />
                      ) : (
                        <>
                          <Box data-testid={`hot-water-${index}-applianceName`}>
                            <b>Appliance Name: </b>
                            {matchingDevice?.applianceName}
                          </Box>
                          <Box data-testid={`hot-water-${index}-oemId`}>
                            <b>Contactor OEM ID: </b>
                            {matchingDevice?.oemId}
                          </Box>
                          {hasMultipleUnconfirmedDevices && (
                            <Box mt={1}>
                              <Checkbox
                                data-testid={`hot-water-${index}-remove-checkbox`}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  removeHotWaterDevice(index);
                                  toast({
                                    title:
                                      'Removed invalid device. To undo, refresh by pulling down at the top of the page.',
                                    status: 'info',
                                    isClosable: true,
                                  });
                                }}
                              >
                                This is not a hot water system.
                              </Checkbox>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                  <AccordionIcon />
                </Flex>
              </AccordionButton>
              <AccordionPanel pb={4} px={[4, 6]}>
                <FormControl
                  isInvalid={!!errors?.hotWaterDevices?.[index]?.hotWaterType}
                  mt={3}
                  id={`${field.id}-type`}
                >
                  <FormLabel>Type</FormLabel>
                  <Controller
                    control={control}
                    name={`hotWaterDevices.${index}.hotWaterType`}
                    render={({ field: { onChange, value, ref } }) => {
                      return (
                        <RadioGroup data-testid={`hot-water-${index}-type`} onChange={onChange} ref={ref} value={value}>
                          <Flex>
                            <Radio
                              data-testid={`hot-water-${index}-type-resistive`}
                              colorScheme="primaryBranding"
                              mr={4}
                              value="resistive"
                            >
                              Resistive
                            </Radio>
                            <Radio
                              data-testid={`hot-water-${index}-type-heatPump`}
                              colorScheme="primaryBranding"
                              mr={4}
                              value="heatPump"
                            >
                              Heat Pump
                            </Radio>
                            <Radio
                              data-testid={`hot-water-${index}-type-solar`}
                              colorScheme="primaryBranding"
                              value="solar"
                            >
                              Solar
                            </Radio>
                          </Flex>
                        </RadioGroup>
                      );
                    }}
                  />
                  <FormErrorMessage data-testid={`hot-water-${index}-type-error`}>
                    {errors?.hotWaterDevices?.[index]?.hotWaterType?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors?.hotWaterDevices?.[index]?.powerRatingkW}
                  mt={3}
                  id={`${field.id}_powerRatingkW`}
                >
                  <FormLabel>
                    Power Rating (kW){' '}
                    <InfoPopover>
                      If you can't determine the power rating, just keep the default value of 3.6.
                    </InfoPopover>
                  </FormLabel>
                  <Input
                    data-testid={`hot-water-${index}-powerRatingkW`}
                    placeholder={'3.6'}
                    defaultValue={3.6}
                    {...register(`hotWaterDevices.${index}.powerRatingkW` as const)}
                    type="number"
                    step="0.1"
                  />
                  <FormErrorMessage data-testid={`hot-water-${index}-powerRatingkW-error`}>
                    {errors?.hotWaterDevices?.[index]?.powerRatingkW?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl mt={3} id={`${field.id}_manufacturerString`}>
                  <FormLabel>
                    Manufacturer <InfoPopover>If you know the manufacturer, please type its name here.</InfoPopover>
                  </FormLabel>
                  <Input
                    data-testid={`hot-water-${index}-manufacturerString`}
                    placeholder={'Enter name of manufacturer'}
                    {...register(`hotWaterDevices.${index}.manufacturerString` as const)}
                    type="text"
                  />
                  <FormErrorMessage>{errors?.hotWaterDevices?.[index]?.manufacturerString?.message}</FormErrorMessage>
                </FormControl>
                <FormControl mt={3} id={`${field.id}_modelNumber`}>
                  <FormLabel>
                    Model Number <InfoPopover>If you can find the model number, please type it here.</InfoPopover>
                  </FormLabel>
                  <Input
                    data-testid={`hot-water-${index}-modelNumber`}
                    placeholder={'Enter the model number'}
                    {...register(`hotWaterDevices.${index}.modelNumber` as const)}
                    type="text"
                  />
                  <FormErrorMessage>{errors?.hotWaterDevices?.[index]?.modelNumber?.message}</FormErrorMessage>
                </FormControl>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Box>
  );
}
